// Der Ereignis-Strom fuer den Dashboard-Feed und das untere Panel.
//
// Vorher lag das direkt in Dashboard.svelte: ein getJSON() im $effect
// plus eine EIGENE, roh aufgemachte WebSocket -- also eine zweite
// Leitung neben dem Hub, ohne dessen Reconnect und ohne die
// Refocus-Revalidierung. Ein Dashboard, das zwei Stunden offen stand,
// konnte den Feed still verlieren (docs/data-loading.md, Refocus).
//
// Live-Weg (seit 02.11.2026): das Protokoll-Topic `meldung`
// (protokoll/meldungen.svelte.ts). Jede gespeicherte Aenderung an den
// Meldungen laedt den Feed gebuendelt (300 ms) neu; eine Luecke ebenso.
// Der 120-s-Poll und der WS-Kanal "events" entfallen, das Netz darunter
// ist der Revalidator (Fokuswechsel, Wiederverbinden).
//
// Aenderung gegenueber dem WS-Weg: die direkt eingefuegten Rahmen mit ts
// (jeder Aufgaben-Lauf, auch der nicht gespeicherte Routine-Erfolg, und
// die MQTT-Link-Ereignisse) entfallen. Der Feed zeigt, was in der
// events-Tabelle steht -- nicht mehr den fluechtigen Aktivitaets-Strom.

import { SvelteMap } from "svelte/reactivity";

import { loadPersisted, persist } from "./persistent-summary.svelte";
import { betrifftEvents, meldungRefetch } from "./protokoll/meldungen.svelte";
import { registerRevalidator } from "./revalidate.svelte";
import type { AppEvent } from "./types";

const STORAGE_KEY = "events";
// 30 statt 10: das Feed-Clustering faltet Task-ok-Salven zu einer Zeile
// zusammen -- damit trotzdem ~10 sichtbare Zeilen bleiben, braucht der
// Puffer mehr Rohmaterial.
const MAX_EVENTS = 30;

const _state = new SvelteMap<string, AppEvent[]>();
let _started = false;
let _unsub: (() => void) | undefined;

async function refresh(): Promise<void> {
  try {
    const r = await fetch(`/v2-api/events?limit=${MAX_EVENTS}`, {
      credentials: "include",
    });
    if (!r.ok) return;
    const data = (await r.json()) as AppEvent[];
    _state.set("current", data);
    persist(STORAGE_KEY, data);
  } catch {
    /* ignore -- die naechste Meldung oder Revalidierung versucht's wieder */
  }
}

export function startEventsSummary(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(refresh);
  const cached = loadPersisted<AppEvent[]>(STORAGE_KEY);
  if (cached) _state.set("current", cached);
  // Die Zeile traegt erst die Datenbank (id) -- also neu laden, gebuendelt.
  // Quittierungen des Banners (alert_acks) stehen nicht im Feed. 2 s statt
  // 300 ms: eine Backup-Welle schreibt ~20 Meldungen in 5 s, der Feed
  // braucht davon einen Abruf, nicht fünfzehn.
  _unsub = meldungRefetch(refresh, { ms: 2_000, filter: betrifftEvents });
  void refresh();
}

export function stopEventsSummary(): void {
  _started = false;
  _unsub?.();
  _unsub = undefined;
  _state.clear();
}

export function getEvents(): AppEvent[] {
  return _state.get("current") ?? [];
}

export function refreshEvents(): Promise<void> {
  return refresh();
}
